import { useEffect, useRef, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useTenant } from '@/providers/tenant-context';
import { useUser } from '@/hooks/use-user';
import axiosInstance from '@/lib/axios';
import { useSWRConfig } from 'swr';
import { AxiosError } from 'axios';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { toast } from 'sonner';
import { Loader2, Users, LogIn, Link2Off, Home } from 'lucide-react';
import {
  clearPersistedInviteCode,
  getCommunityDashboardPath,
  getLoginPathForInvite,
  persistInviteCode,
} from '@/lib/invite-flow';

interface CommunityDetails {
  id: string;
  name: string;
  logo: string;
  slug: string;
  memberCount: number;
}

export default function InvitePage() {
  const { code } = useParams();
  const navigate = useNavigate();
  const { mutate } = useSWRConfig();
  const { tenant } = useTenant();
  const { user, isLoading: isUserLoading, communities } = useUser();
  const platformName = tenant?.name ?? 'Milora';
  const [community, setCommunity] = useState<CommunityDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [joining, setJoining] = useState(false);
  const [error, setError] = useState('');
  const autoJoinStarted = useRef(false);

  useEffect(() => {
    if (code) persistInviteCode(code);
  }, [code]);

  useEffect(() => {
    async function fetchInvite() {
      try {
        const res = await axiosInstance.get(`/api/invites/${code}`);
        setCommunity(res.data);
      } catch (err) {
        const axiosErr = err as AxiosError<{ error: string }>;
        setError(axiosErr.response?.data?.error || 'Invalid invite link');
      } finally {
        setLoading(false);
      }
    }
    if (code) fetchInvite();
  }, [code]);

  useEffect(() => {
    if (!user || !community || !code || isUserLoading || autoJoinStarted.current) return;

    const membership = communities.find((c) => c.id === community.id);
    if (membership?.status === 'JOINED') {
      clearPersistedInviteCode();
      navigate(getCommunityDashboardPath(community.id), { replace: true });
      return;
    }

    autoJoinStarted.current = true;
    setJoining(true);

    axiosInstance
      .post(`/api/communities/${community.id}/join`, { inviteCode: code })
      .then(async () => {
        clearPersistedInviteCode();
        await Promise.all([mutate('/api/communities'), mutate('/api/user')]);
        navigate(getCommunityDashboardPath(community.id), { replace: true });
      })
      .catch(async (err) => {
        const axiosErr = err as AxiosError<{ error: string }>;
        if (axiosErr.response?.status === 409) {
          clearPersistedInviteCode();
          await Promise.all([mutate('/api/communities'), mutate('/api/user')]);
          navigate(getCommunityDashboardPath(community.id), { replace: true });
          return;
        }
        autoJoinStarted.current = false;
        toast.error(axiosErr.response?.data?.error || 'Failed to join community');
      })
      .finally(() => {
        setJoining(false);
      });
  }, [user, community, code, communities, isUserLoading, navigate, mutate]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (error || !community) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4 bg-background relative overflow-hidden">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-primary/10 rounded-full blur-[128px] -z-10" />
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-primary/10 rounded-full blur-[128px] -z-10" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-destructive/5 rounded-full blur-[160px] -z-10" />

        <Card className="w-full max-w-md border-border/50 bg-background/60 backdrop-blur-xl shadow-2xl text-center">
          <CardHeader className="pb-2 pt-8">
            <div className="mx-auto mb-5 w-20 h-20 rounded-2xl bg-destructive/10 border border-destructive/20 flex items-center justify-center shadow-lg">
              <Link2Off className="w-9 h-9 text-destructive" />
            </div>
            <CardTitle className="text-2xl font-bold text-foreground">Link Unavailable</CardTitle>
            <CardDescription className="text-sm text-muted-foreground mt-2 leading-relaxed px-2">
              {error || 'This invite link is no longer valid.'}
            </CardDescription>
          </CardHeader>
          <CardContent className="pb-2">
            <div className="w-full h-px bg-border/50 my-2" />
          </CardContent>
          <CardFooter className="flex flex-col gap-3 pb-8 px-8">
            <Button className="w-full h-11 font-semibold" asChild>
              <Link to="/">
                <Home className="mr-2 w-4 h-4" />
                Go to Home
              </Link>
            </Button>
          </CardFooter>
        </Card>
      </div>
    );
  }

  if (user) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-background relative overflow-hidden">
      <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-primary/10 rounded-full blur-[128px] -z-10" />
      <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-primary/10 rounded-full blur-[128px] -z-10" />

      <Card className="w-full max-w-md border-border/50 bg-background/60 backdrop-blur-xl shadow-2xl">
        <CardHeader className="text-center pb-2">
          <div className="mx-auto mb-4 w-20 h-20 rounded-2xl bg-secondary flex items-center justify-center overflow-hidden shadow-lg border border-border">
            {community.logo ? (
              <img
                src={community.logo}
                alt={community.name}
                className="w-full h-full object-cover"
              />
            ) : (
              <span className="text-3xl font-bold text-primary">{community.name.charAt(0)}</span>
            )}
          </div>
          <CardTitle className="text-2xl font-bold">{community.name}</CardTitle>
          <CardDescription className="flex items-center justify-center gap-2 mt-2">
            <Users className="w-4 h-4" />
            {community.memberCount} members
          </CardDescription>
        </CardHeader>
        <CardContent className="text-center pt-4">
          <p className="text-muted-foreground text-sm">
            You have been invited to join this community on {platformName}. Log in or create an
            account to continue.
          </p>
        </CardContent>
        <CardFooter className="flex flex-col gap-3 pb-8 px-8">
          {isUserLoading || joining ? (
            <Button disabled className="w-full h-12">
              <Loader2 className="h-5 w-5 animate-spin" />
            </Button>
          ) : (
            <Button className="w-full h-12 text-lg font-semibold" asChild>
              <Link to={code ? getLoginPathForInvite(code) : '/login'}>
                <LogIn className="mr-2 w-5 h-5" />
                Log in to Join
              </Link>
            </Button>
          )}
        </CardFooter>
      </Card>
    </div>
  );
}
